import { useState, useEffect } from "react";
import { AppLayout } from "@/components/AppLayout";
import { Button } from "@/components/ui/button";
import { useToast } from "@/hooks/use-toast";
import { useAuth } from "@/hooks/useAuth";
import { useSubscription } from "@/hooks/useSubscription";
import { useNavigate, useSearchParams, useLocation } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { 
  Lock, 
  Shield, 
  Check, 
  Sparkles,
  Zap,
  Crown,
  ArrowRight,
  Smartphone,
  Wallet,
  AlertCircle,
  ArrowLeft,
  CreditCard,
  Globe,
  MessageCircle,
  RefreshCw
} from "lucide-react";
import { cn } from "@/lib/utils";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";

// FCFA to USD conversion rate (approximate)
const FCFA_TO_USD = 0.0016;

// PayPal email for direct payments
const PAYPAL_EMAIL = "aifortune35@gmail.com";

// Credit packs for one-time purchase (standardized)
const creditPacks = [
  {
    id: "starter",
    name: "Starter",
    credits: 50,
    priceFCFA: 4900,
    priceUSD: 8,
    popular: false,
    icon: Zap,
    description: "Parfait pour découvrir Floop",
    pricePerCreditFCFA: "98 FCFA",
    pricePerCreditUSD: "$0.16",
  },
  {
    id: "creator",
    name: "Créateur",
    credits: 150,
    priceFCFA: 12500,
    priceUSD: 20,
    popular: true,
    icon: Sparkles,
    description: "Le choix des créateurs réguliers",
    pricePerCreditFCFA: "83 FCFA",
    pricePerCreditUSD: "$0.13",
    savings: "15%",
  },
  {
    id: "pro",
    name: "Pro",
    credits: 350,
    priceFCFA: 24900,
    priceUSD: 40,
    popular: false,
    icon: Crown,
    description: "Pour les utilisateurs intensifs",
    pricePerCreditFCFA: "71 FCFA",
    pricePerCreditUSD: "$0.11",
    savings: "28%",
  },
  {
    id: "studio",
    name: "Studio",
    credits: 750,
    priceFCFA: 59900,
    priceUSD: 96,
    popular: false,
    icon: Crown,
    description: "Pour les professionnels et studios",
    pricePerCreditFCFA: "80 FCFA",
    pricePerCreditUSD: "$0.13",
    savings: "18%",
  },
];

type PaymentMethod = "lygos" | "paypal";

interface SubscriptionData {
  planId: string;
  planName: string;
  price: number;
  priceUSD?: number;
  credits: number;
  period: "monthly" | "annual";
}

const Payment = () => {
  const { toast } = useToast();
  const { user, isAuthenticated, loading: authLoading } = useAuth();
  const { isPro, isLoading: subscriptionLoading, plan } = useSubscription();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const [selectedPack, setSelectedPack] = useState("creator");
  const [isProcessing, setIsProcessing] = useState(false);
  const [activeTab, setActiveTab] = useState<"credits" | "subscription">("credits");
  const [paymentMethod, setPaymentMethod] = useState<PaymentMethod>("lygos");
  const [showPaymentErrorDialog, setShowPaymentErrorDialog] = useState(false);
  const [paymentErrorMessage, setPaymentErrorMessage] = useState("");

  // Get subscription data from navigation state (from Pricing page)
  const subscriptionData = (location.state as { subscription?: SubscriptionData })?.subscription;

  const selectedPackData = creditPacks.find(p => p.id === selectedPack);

  const WHATSAPP_NUMBER = "+237697747365";
  const WHATSAPP_MESSAGE = encodeURIComponent("Bonjour, j'ai rencontré un problème lors de mon paiement sur Floop. Pouvez-vous m'aider ?");

  // Auto-select subscription tab if coming from pricing page
  useEffect(() => {
    if (subscriptionData) {
      setActiveTab("subscription");
    }
  }, [subscriptionData]);

  // Check for payment status redirects
  useEffect(() => {
    const paymentStatus = searchParams.get('payment');
    const orderId = searchParams.get('order_id');
    
    if (paymentStatus === 'failed') {
      setPaymentErrorMessage("Le paiement n'a pas abouti. Veuillez réessayer ou contactez-nous pour une assistance rapide.");
      setShowPaymentErrorDialog(true);
    }
    
    // Handle PayPal success redirect
    if (paymentStatus === 'success' && orderId) {
      handlePayPalCapture(orderId);
    }
  }, [searchParams]);

  // Redirect to auth if not logged in
  useEffect(() => {
    if (!authLoading && !isAuthenticated) {
      navigate('/auth', { state: { from: '/payment', subscription: subscriptionData } });
    }
  }, [authLoading, isAuthenticated, navigate, subscriptionData]);

  const handlePayPalCapture = async (orderId: string) => {
    setIsProcessing(true);
    try {
      const { data, error } = await supabase.functions.invoke('paypal-capture-order', {
        body: { order_id: orderId },
      });

      if (error) throw error;

      if (data?.success) {
        localStorage.setItem('pending_order_id', orderId);
        navigate('/payment-success');
      } else {
        throw new Error('Capture failed');
      }
    } catch (error: any) {
      console.error('PayPal capture error:', error);
      toast({
        title: "Erreur de paiement",
        description: error.message || "Erreur lors de la finalisation du paiement PayPal",
        variant: "destructive",
      });
      setIsProcessing(false);
    }
  };

  const handleLygosPurchase = async (amount: number, credits: number, packName: string) => {
    if (!isAuthenticated) {
      navigate('/auth', { state: { from: '/payment', subscription: subscriptionData } });
      return;
    }

    setIsProcessing(true);
    
    try {
      const { data, error } = await supabase.functions.invoke('lygos-payment', {
        body: {
          amount,
          credits,
          pack_name: packName,
          failure_url: `${window.location.origin}/payment?payment=failed`,
        },
      });

      if (error) {
        console.error('Payment error:', error);
        throw new Error(error.message || 'Erreur lors de la création du paiement');
      }

      if (data?.payment_url && data?.order_id) {
        localStorage.setItem('pending_order_id', data.order_id);
        window.location.href = data.payment_url;
      } else {
        throw new Error('URL de paiement non reçue');
      }
    } catch (error: any) {
      console.error('Payment error:', error);
      setPaymentErrorMessage(error.message || "Une erreur est survenue lors du paiement.");
      setShowPaymentErrorDialog(true);
      setIsProcessing(false);
    }
  };

  const handlePayPalPurchase = async (amount: number, credits: number, packName: string) => {
    // Use PayPal.me link for direct payment
    const paypalUrl = `https://www.paypal.me/aifortune35/${amount}USD`;
    
    // Store pending payment info for manual verification
    const orderId = `FLOOP-${Date.now()}-${Math.random().toString(36).substring(2, 9)}`;
    localStorage.setItem('pending_paypal_order', JSON.stringify({
      orderId,
      amount,
      credits,
      packName,
      userId: user?.id,
      createdAt: new Date().toISOString(),
    }));
    
    toast({
      title: "Redirection vers PayPal",
      description: `Envoyez ${amount} USD à ${PAYPAL_EMAIL}. Après paiement, contactez-nous avec votre ID: ${orderId.slice(0, 15)}...`,
    });
    
    // Open PayPal.me in a new tab
    window.open(paypalUrl, '_blank');
    setIsProcessing(false);
  };

  const handleCreditPurchase = async () => {
    if (!isAuthenticated || !user) {
      navigate('/auth', { state: { from: '/payment' } });
      return;
    }

    if (!selectedPackData) {
      toast({
        title: "Erreur",
        description: "Veuillez sélectionner un pack",
        variant: "destructive",
      });
      return;
    }

    if (paymentMethod === "lygos") {
      await handleLygosPurchase(selectedPackData.priceFCFA, selectedPackData.credits, selectedPackData.name);
    } else {
      await handlePayPalPurchase(selectedPackData.priceUSD, selectedPackData.credits, selectedPackData.name);
    }
  };

  const handleSubscriptionPurchase = async () => {
    if (!isAuthenticated || !user) {
      navigate('/auth', { state: { from: '/payment', subscription: subscriptionData } });
      return;
    }

    if (!subscriptionData) {
      toast({
        title: "Erreur",
        description: "Données d'abonnement manquantes",
        variant: "destructive",
      });
      return;
    }

    const packName = `Abonnement ${subscriptionData.planName} (${subscriptionData.period === 'annual' ? 'Annuel' : 'Mensuel'})`;

    if (paymentMethod === "lygos") {
      await handleLygosPurchase(subscriptionData.price, subscriptionData.credits, packName);
    } else {
      const usdPrice = subscriptionData.priceUSD || Math.round(subscriptionData.price * FCFA_TO_USD);
      await handlePayPalPurchase(usdPrice, subscriptionData.credits, packName);
    }
  };

  const formatPrice = (price: number) => {
    return new Intl.NumberFormat('fr-FR').format(price);
  };

  const formatUSD = (price: number) => {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(price);
  };

  if (authLoading) {
    return (
      <AppLayout title="Paiement">
        <div className="flex items-center justify-center h-96">
          <div className="w-8 h-8 border-4 border-primary/30 border-t-primary rounded-full animate-spin" />
        </div>
      </AppLayout>
    );
  }

  return (
    <AppLayout title="Paiement">
      {/* Payment Error Dialog */}
      <Dialog open={showPaymentErrorDialog} onOpenChange={setShowPaymentErrorDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-destructive">
              <AlertCircle className="w-5 h-5" />
              Problème de paiement
            </DialogTitle>
            <DialogDescription className="pt-2">
              {paymentErrorMessage}
            </DialogDescription>
          </DialogHeader>
          
          <div className="flex flex-col gap-3 mt-4">
            <Button 
              onClick={() => {
                setShowPaymentErrorDialog(false);
                // Clear the URL params
                navigate('/payment', { replace: true });
              }}
              className="w-full bg-gradient-floop hover:opacity-90 text-white"
            >
              <RefreshCw className="w-4 h-4 mr-2" />
              Réessayer le paiement
            </Button>
            
            <Button
              variant="outline"
              onClick={() => {
                window.open(`https://wa.me/${WHATSAPP_NUMBER}?text=${WHATSAPP_MESSAGE}`, '_blank');
              }}
              className="w-full border-green-500 text-green-600 hover:bg-green-50 dark:hover:bg-green-950"
            >
              <MessageCircle className="w-4 h-4 mr-2" />
              Contacter sur WhatsApp
            </Button>
          </div>
          
          <p className="text-xs text-muted-foreground text-center mt-3">
            Notre équipe est disponible pour vous aider rapidement
          </p>
        </DialogContent>
      </Dialog>

      <div className="p-4 md:p-6 max-w-6xl mx-auto">
        {/* Back button */}
        <Button
          variant="ghost"
          onClick={() => navigate(-1)}
          className="mb-4 md:mb-6 text-muted-foreground hover:text-foreground"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Retour
        </Button>

        <Tabs value={activeTab} onValueChange={(v) => setActiveTab(v as "credits" | "subscription")} className="space-y-6 md:space-y-8">
          <TabsList className="grid w-full max-w-md grid-cols-2 mx-auto bg-secondary">
            <TabsTrigger value="credits" className="text-xs md:text-sm gap-1.5">
              Acheter des crédits
              {!isPro && <Badge variant="secondary" className="text-[10px] px-1.5 py-0">Pro requis</Badge>}
            </TabsTrigger>
            <TabsTrigger value="subscription" disabled={!subscriptionData} className="text-xs md:text-sm">
              Abonnement
            </TabsTrigger>
          </TabsList>

          {/* Credit Packs Tab */}
          <TabsContent value="credits">
            {!isPro ? (
              <div className="flex flex-col items-center justify-center py-12 md:py-20 px-4 text-center">
                <div className="w-16 h-16 md:w-20 md:h-20 rounded-full bg-secondary flex items-center justify-center mb-6">
                  <Lock className="w-8 h-8 md:w-10 md:h-10 text-muted-foreground" />
                </div>
                <h2 className="text-xl md:text-2xl font-bold text-foreground mb-3 font-display">
                  Abonnement requis
                </h2>
                <p className="text-sm md:text-base text-muted-foreground mb-6 max-w-md">
                  Pour acheter des packs de crédits supplémentaires, vous devez d'abord souscrire à un abonnement Pro ou Studio Max.
                </p>
                <Button
                  onClick={() => navigate('/pricing')}
                  className="bg-gradient-floop hover:opacity-90 text-white px-6 py-3"
                >
                  <Sparkles className="w-4 h-4 mr-2" />
                  Voir les abonnements
                </Button>
              </div>
            ) : (
            <div className="grid lg:grid-cols-5 gap-6 lg:gap-8">
              {/* Credit Packs Selection */}
              <div className="lg:col-span-3 space-y-4 md:space-y-6">
                <div>
                  <h2 className="text-xl md:text-2xl font-bold text-foreground mb-2 font-display">
                    Choisissez votre pack
                  </h2>
                  <p className="text-sm md:text-base text-muted-foreground">
                    Plus vous achetez, plus vous économisez
                  </p>
                </div>

                <div className="grid gap-3 md:gap-4">
                  {creditPacks.map((pack) => (
                    <button
                      key={pack.id}
                      onClick={() => setSelectedPack(pack.id)}
                      className={cn(
                        "relative w-full text-left p-4 md:p-6 rounded-2xl border-2 transition-all duration-300",
                        selectedPack === pack.id
                          ? "border-primary bg-primary/5 shadow-lg shadow-primary/10"
                          : "border-border bg-card hover:border-primary/50"
                      )}
                    >
                      {pack.popular && (
                        <div className="absolute -top-3 left-4 md:left-6 bg-gradient-floop text-white text-xs font-bold px-2 md:px-3 py-1 rounded-full">
                          Populaire
                        </div>
                      )}
                      
                      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                        <div className="flex items-center gap-3 md:gap-4">
                          <div className={cn(
                            "w-10 h-10 md:w-14 md:h-14 rounded-xl flex items-center justify-center transition-colors flex-shrink-0",
                            selectedPack === pack.id
                              ? "bg-gradient-floop"
                              : "bg-secondary"
                          )}>
                            <pack.icon className={cn(
                              "w-5 h-5 md:w-7 md:h-7",
                              selectedPack === pack.id ? "text-white" : "text-primary"
                            )} />
                          </div>
                          <div className="min-w-0">
                            <div className="flex items-center gap-2 flex-wrap">
                              <h3 className="text-base md:text-lg font-bold text-foreground">{pack.name}</h3>
                              {pack.savings && (
                                <span className="text-xs bg-success/20 text-success px-2 py-0.5 rounded-full font-medium">
                                  -{pack.savings}
                                </span>
                              )}
                            </div>
                            <p className="text-xs md:text-sm text-muted-foreground hidden sm:block">{pack.description}</p>
                            <p className="text-xs text-muted-foreground mt-0.5">
                              {paymentMethod === "lygos" ? pack.pricePerCreditFCFA : pack.pricePerCreditUSD}/crédit
                            </p>
                          </div>
                        </div>
                        
                        <div className="text-left sm:text-right flex sm:flex-col items-center sm:items-end gap-2 sm:gap-0">
                          <div className="flex items-center gap-1 md:gap-2">
                            <span className="text-xl md:text-3xl font-bold text-foreground">{pack.credits}</span>
                            <span className="text-xs md:text-sm text-muted-foreground">crédits</span>
                          </div>
                          <div className="text-base md:text-xl font-bold text-primary">
                            {paymentMethod === "lygos" 
                              ? `${formatPrice(pack.priceFCFA)} FCFA`
                              : formatUSD(pack.priceUSD)
                            }
                          </div>
                        </div>
                      </div>
                      
                      <div className={cn(
                        "absolute right-3 md:right-4 top-4 md:top-1/2 md:-translate-y-1/2 w-5 h-5 md:w-6 md:h-6 rounded-full border-2 flex items-center justify-center transition-colors",
                        selectedPack === pack.id
                          ? "border-primary bg-primary"
                          : "border-muted"
                      )}>
                        {selectedPack === pack.id && (
                          <Check className="w-3 h-3 md:w-4 md:h-4 text-white" />
                        )}
                      </div>
                    </button>
                  ))}
                </div>

                {/* What you get */}
                <div className="bg-card border border-border rounded-2xl p-4 md:p-6">
                  <h3 className="font-semibold text-foreground mb-3 md:mb-4 text-sm md:text-base">Ce que vous pouvez créer avec {selectedPackData?.credits} crédits</h3>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-2 md:gap-4">
                    <div className="text-center p-2 md:p-3 bg-secondary/50 rounded-xl">
                      <div className="text-lg md:text-2xl font-bold text-primary">{Math.floor((selectedPackData?.credits || 0) / 3)}</div>
                      <div className="text-xs text-muted-foreground">Floop 4K</div>
                    </div>
                    <div className="text-center p-2 md:p-3 bg-secondary/50 rounded-xl">
                      <div className="text-lg md:text-2xl font-bold text-accent">{Math.floor((selectedPackData?.credits || 0) / 5)}</div>
                      <div className="text-xs text-muted-foreground">Pro HD</div>
                    </div>
                    <div className="text-center p-2 md:p-3 bg-secondary/50 rounded-xl">
                      <div className="text-lg md:text-2xl font-bold text-highlight">{Math.floor((selectedPackData?.credits || 0) / 10)}</div>
                      <div className="text-xs text-muted-foreground">Pro 4K</div>
                    </div>
                    <div className="text-center p-2 md:p-3 bg-secondary/50 rounded-xl">
                      <div className="text-lg md:text-2xl font-bold text-violet">{selectedPackData?.credits}</div>
                      <div className="text-xs text-muted-foreground">Floop HD</div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Payment Summary for Credits */}
              <div className="lg:col-span-2">
                <PaymentSummary
                  title={`Pack ${selectedPackData?.name}`}
                  credits={selectedPackData?.credits || 0}
                  priceFCFA={selectedPackData?.priceFCFA || 0}
                  priceUSD={selectedPackData?.priceUSD || 0}
                  pricePerCredit={paymentMethod === "lygos" ? selectedPackData?.pricePerCreditFCFA : selectedPackData?.pricePerCreditUSD}
                  isProcessing={isProcessing}
                  onPay={handleCreditPurchase}
                  formatPrice={formatPrice}
                  formatUSD={formatUSD}
                  paymentMethod={paymentMethod}
                  onPaymentMethodChange={setPaymentMethod}
                />
              </div>
            </div>
            )}
          </TabsContent>

          {/* Subscription Tab */}
          <TabsContent value="subscription">
            {subscriptionData ? (
              <div className="grid lg:grid-cols-5 gap-8">
                <div className="lg:col-span-3 space-y-6">
                  <div>
                    <h2 className="text-2xl font-bold text-foreground mb-2 font-display">
                      Votre abonnement
                    </h2>
                    <p className="text-muted-foreground">
                      Vous avez choisi l'abonnement {subscriptionData.planName}
                    </p>
                  </div>

                  {/* Subscription details card */}
                  <div className="bg-card border border-primary/30 rounded-2xl p-8 relative overflow-hidden">
                    <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-floop opacity-10 blur-3xl" />
                    
                    <div className="flex items-center gap-4 mb-6">
                      <div className="w-16 h-16 rounded-2xl bg-gradient-floop flex items-center justify-center">
                        <Crown className="w-8 h-8 text-white" />
                      </div>
                      <div>
                        <h3 className="text-2xl font-bold text-foreground">{subscriptionData.planName}</h3>
                        <p className="text-muted-foreground">
                          {subscriptionData.period === 'annual' ? 'Facturation annuelle' : 'Facturation mensuelle'}
                        </p>
                      </div>
                    </div>

                    <div className="space-y-4">
                      <div className="flex items-center gap-3">
                        <Check className="w-5 h-5 text-success" />
                        <span className="text-foreground">{subscriptionData.credits} crédits {subscriptionData.period === 'annual' ? 'par an' : 'par mois'}</span>
                      </div>
                      <div className="flex items-center gap-3">
                        <Check className="w-5 h-5 text-success" />
                        <span className="text-foreground">Accès à tous les modèles Floop</span>
                      </div>
                      <div className="flex items-center gap-3">
                        <Check className="w-5 h-5 text-success" />
                        <span className="text-foreground">Crédits reportables au mois suivant</span>
                      </div>
                      <div className="flex items-center gap-3">
                        <Check className="w-5 h-5 text-success" />
                        <span className="text-foreground">Annulation à tout moment</span>
                      </div>
                    </div>
                  </div>

                  {/* Want credits instead? */}
                  <div className="bg-secondary/50 border border-border rounded-xl p-4 flex items-center justify-between">
                    <span className="text-muted-foreground">Préférez acheter des crédits à l'unité ?</span>
                    <Button 
                      variant="outline" 
                      size="sm"
                      onClick={() => {
                        setActiveTab("credits");
                        navigate('/payment', { replace: true });
                      }}
                    >
                      Voir les packs
                    </Button>
                  </div>
                </div>

                {/* Payment Summary for Subscription */}
                <div className="lg:col-span-2">
                  <PaymentSummary
                    title={`Abonnement ${subscriptionData.planName}`}
                    subtitle={subscriptionData.period === 'annual' ? 'Facturation annuelle' : 'Facturation mensuelle'}
                    credits={subscriptionData.credits}
                    priceFCFA={subscriptionData.price}
                    priceUSD={subscriptionData.priceUSD || Math.round(subscriptionData.price * FCFA_TO_USD)}
                    isProcessing={isProcessing}
                    onPay={handleSubscriptionPurchase}
                    formatPrice={formatPrice}
                    formatUSD={formatUSD}
                    paymentMethod={paymentMethod}
                    onPaymentMethodChange={setPaymentMethod}
                  />
                </div>
              </div>
            ) : (
              <div className="text-center py-12">
                <p className="text-muted-foreground mb-4">Aucun abonnement sélectionné</p>
                <Button onClick={() => navigate('/pricing')}>
                  Voir les plans
                </Button>
              </div>
            )}
          </TabsContent>
        </Tabs>
      </div>
    </AppLayout>
  );
};

// Payment Summary Component
interface PaymentSummaryProps {
  title: string;
  subtitle?: string;
  credits: number;
  priceFCFA: number;
  priceUSD: number;
  pricePerCredit?: string;
  isProcessing: boolean;
  onPay: () => void;
  formatPrice: (price: number) => string;
  formatUSD: (price: number) => string;
  paymentMethod: PaymentMethod;
  onPaymentMethodChange: (method: PaymentMethod) => void;
}

const PaymentSummary = ({ 
  title, 
  subtitle,
  credits, 
  priceFCFA,
  priceUSD,
  pricePerCredit, 
  isProcessing, 
  onPay, 
  formatPrice,
  formatUSD,
  paymentMethod,
  onPaymentMethodChange,
}: PaymentSummaryProps) => {
  return (
    <div className="bg-card border border-border rounded-2xl p-6 sticky top-6 space-y-6">
      <div>
        <h3 className="text-xl font-bold text-foreground mb-1">Mode de paiement</h3>
        <p className="text-sm text-muted-foreground">Choisissez votre méthode préférée</p>
      </div>

      {/* Payment Method Selection */}
      <RadioGroup 
        value={paymentMethod} 
        onValueChange={(v) => onPaymentMethodChange(v as PaymentMethod)}
        className="space-y-3"
      >
        {/* Lygos / Mobile Money */}
        <div className={cn(
          "flex items-center space-x-3 rounded-xl border-2 p-4 cursor-pointer transition-all",
          paymentMethod === "lygos" 
            ? "border-primary bg-primary/5" 
            : "border-border hover:border-primary/50"
        )}>
          <RadioGroupItem value="lygos" id="lygos" />
          <Label htmlFor="lygos" className="flex items-center gap-3 cursor-pointer flex-1">
            <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-orange-500 to-yellow-500 flex items-center justify-center">
              <Smartphone className="w-5 h-5 text-white" />
            </div>
            <div>
              <p className="font-semibold text-foreground">Mobile Money</p>
              <p className="text-xs text-muted-foreground">Orange, MTN, Wave, Moov</p>
            </div>
          </Label>
          <div className="text-right">
            <p className="font-bold text-foreground">{formatPrice(priceFCFA)}</p>
            <p className="text-xs text-muted-foreground">FCFA</p>
          </div>
        </div>

        {/* PayPal - Coming Soon */}
        <div className="flex items-center space-x-3 rounded-xl border-2 p-4 opacity-60 cursor-not-allowed border-border bg-secondary/30">
          <RadioGroupItem value="paypal" id="paypal" disabled />
          <Label htmlFor="paypal" className="flex items-center gap-3 flex-1">
            <div className="w-10 h-10 rounded-lg bg-[#003087]/50 flex items-center justify-center">
              <span className="text-white font-bold text-sm">PP</span>
            </div>
            <div>
              <p className="font-semibold text-foreground flex items-center gap-2">
                PayPal
                <Badge variant="secondary" className="text-xs">Bientôt</Badge>
              </p>
              <p className="text-xs text-muted-foreground">Carte bancaire, PayPal</p>
            </div>
          </Label>
          <div className="text-right">
            <p className="font-bold text-foreground">{formatUSD(priceUSD)}</p>
            <p className="text-xs text-muted-foreground">USD</p>
          </div>
        </div>
      </RadioGroup>

      {/* Order Summary */}
      <div className="bg-secondary/50 rounded-xl p-4 space-y-3">
        <div className="flex items-center justify-between">
          <span className="text-muted-foreground">{title}</span>
          <span className="font-medium text-foreground">{credits} crédits</span>
        </div>
        {subtitle && (
          <div className="flex items-center justify-between">
            <span className="text-muted-foreground">Période</span>
            <span className="text-foreground">{subtitle}</span>
          </div>
        )}
        {pricePerCredit && (
          <div className="flex items-center justify-between">
            <span className="text-muted-foreground">Prix unitaire</span>
            <span className="text-foreground">{pricePerCredit}/crédit</span>
          </div>
        )}
        <div className="border-t border-border pt-3">
          <div className="flex items-center justify-between">
            <span className="font-semibold text-foreground">Total</span>
            <span className="text-2xl font-bold bg-gradient-floop bg-clip-text text-transparent">
              {paymentMethod === "lygos" 
                ? `${formatPrice(priceFCFA)} FCFA`
                : formatUSD(priceUSD)
              }
            </span>
          </div>
        </div>
      </div>

      {/* Payment Methods Info for selected method */}
      {paymentMethod === "lygos" && (
        <div className="space-y-3">
          <p className="text-sm text-muted-foreground">Méthodes acceptées :</p>
          <div className="grid grid-cols-2 gap-2">
            <div className="flex items-center gap-2 bg-secondary/50 rounded-lg p-2">
              <div className="w-6 h-6 bg-orange-500 rounded flex items-center justify-center">
                <span className="text-white text-xs font-bold">OM</span>
              </div>
              <span className="text-xs text-foreground">Orange Money</span>
            </div>
            <div className="flex items-center gap-2 bg-secondary/50 rounded-lg p-2">
              <div className="w-6 h-6 bg-yellow-500 rounded flex items-center justify-center">
                <span className="text-white text-xs font-bold">MTN</span>
              </div>
              <span className="text-xs text-foreground">MTN MoMo</span>
            </div>
            <div className="flex items-center gap-2 bg-secondary/50 rounded-lg p-2">
              <div className="w-6 h-6 bg-blue-500 rounded flex items-center justify-center">
                <span className="text-white text-xs font-bold">W</span>
              </div>
              <span className="text-xs text-foreground">Wave</span>
            </div>
            <div className="flex items-center gap-2 bg-secondary/50 rounded-lg p-2">
              <div className="w-6 h-6 bg-green-500 rounded flex items-center justify-center">
                <Wallet className="w-3 h-3 text-white" />
              </div>
              <span className="text-xs text-foreground">Moov Money</span>
            </div>
          </div>
        </div>
      )}

      {paymentMethod === "paypal" && (
        <div className="space-y-3">
          <p className="text-sm text-muted-foreground">Méthodes acceptées :</p>
          <div className="grid grid-cols-2 gap-2">
            <div className="flex items-center gap-2 bg-secondary/50 rounded-lg p-2">
              <div className="w-6 h-6 bg-[#003087] rounded flex items-center justify-center">
                <span className="text-white text-xs font-bold">PP</span>
              </div>
              <span className="text-xs text-foreground">PayPal</span>
            </div>
            <div className="flex items-center gap-2 bg-secondary/50 rounded-lg p-2">
              <div className="w-6 h-6 bg-gradient-to-r from-blue-600 to-blue-400 rounded flex items-center justify-center">
                <CreditCard className="w-3 h-3 text-white" />
              </div>
              <span className="text-xs text-foreground">Visa / MC</span>
            </div>
            <div className="flex items-center gap-2 bg-secondary/50 rounded-lg p-2">
              <div className="w-6 h-6 bg-black rounded flex items-center justify-center">
                <span className="text-white text-xs font-bold">A</span>
              </div>
              <span className="text-xs text-foreground">Amex</span>
            </div>
            <div className="flex items-center gap-2 bg-secondary/50 rounded-lg p-2">
              <div className="w-6 h-6 bg-gradient-to-r from-orange-500 to-red-500 rounded flex items-center justify-center">
                <Globe className="w-3 h-3 text-white" />
              </div>
              <span className="text-xs text-foreground">International</span>
            </div>
          </div>
        </div>
      )}

      {/* Pay Button */}
      <Button
        onClick={onPay}
        disabled={isProcessing}
        className={cn(
          "w-full rounded-xl py-6 text-lg font-semibold group",
          paymentMethod === "paypal" 
            ? "bg-[#003087] hover:bg-[#001f5c] text-white"
            : "bg-gradient-floop hover:opacity-90 text-white"
        )}
      >
        {isProcessing ? (
          <div className="flex items-center gap-2">
            <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
            {paymentMethod === "lygos" ? "Redirection vers Lygos..." : "Redirection vers PayPal..."}
          </div>
        ) : (
          <>
            Payer {paymentMethod === "lygos" ? `${formatPrice(priceFCFA)} FCFA` : formatUSD(priceUSD)}
            <ArrowRight className="w-5 h-5 ml-2 group-hover:translate-x-1 transition-transform" />
          </>
        )}
      </Button>

      {/* Info Notice */}
      <div className="flex items-start gap-3 bg-primary/5 border border-primary/20 rounded-xl p-4">
        <AlertCircle className="w-5 h-5 text-primary shrink-0 mt-0.5" />
        <div className="text-sm text-muted-foreground">
          Vous serez redirigé vers{" "}
          <strong className="text-foreground">
            {paymentMethod === "lygos" ? "Lygos" : "PayPal"}
          </strong>{" "}
          pour finaliser votre paiement en toute sécurité.
        </div>
      </div>

      {/* Trust Badges */}
      <div className="flex items-center justify-center gap-4 pt-2">
        <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
          <Shield className="w-4 h-4" />
          Paiement sécurisé
        </div>
        <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
          <Lock className="w-4 h-4" />
          Données protégées
        </div>
      </div>
    </div>
  );
};

export default Payment;
